'use client';

import { checkIsAdminRole, useSession } from '@repo/auth';
import { useEffect } from 'react';
import { useLocalePathname, useLocaleRouter } from '@/lib/i18n/navigation';
import { isEditableTarget } from '@/lib/is-editable-target';
import { isOnSysRoute } from '@/lib/is-on-sys-route';

interface IShortcutState {
  isLoggedIn: boolean;
  isAdmin: boolean;
}

function hasModifier(e: KeyboardEvent): boolean {
  return e.metaKey || e.ctrlKey || e.altKey || e.shiftKey;
}

function resolveShortcutPath(key: string, { isLoggedIn, isAdmin }: IShortcutState): string | null {
  if (key === 'l' && !isLoggedIn) {
    return '/login';
  }
  if (key === 'a' && isLoggedIn) {
    return '/my';
  }
  if (key === 's' && isAdmin) {
    return '/sys';
  }
  return null;
}

export function useNavShortcuts(): void {
  const router = useLocaleRouter();
  const pathname = useLocalePathname();
  const { data: sessionData, isPending } = useSession();
  const isLoggedIn = Boolean(sessionData?.session);
  const isAdmin = checkIsAdminRole({ role: sessionData?.user?.role });

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent): void {
      if (isPending || hasModifier(e) || isEditableTarget(e.target) || isOnSysRoute(pathname)) {
        return;
      }
      const path = resolveShortcutPath(e.key.toLowerCase(), { isLoggedIn, isAdmin });
      if (path) {
        e.preventDefault();
        router.push(path);
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isLoggedIn, isAdmin, isPending, pathname, router]);
}
